import { supabase } from '../lib/supabase';
import { schedulerService } from './schedulerService';
import { bibleService } from './bibleService';

// =============================================================================
// AUTHENTICATION SERVICES
// =============================================================================

export const authService = {
  async signUp(email, password, userData = {}) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: userData?.fullName || '',
            role: userData?.role || 'user'
          }
        }
      });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      return { error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async getSession() {
    try {
      const { data, error } = await supabase?.auth?.getSession();
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      // Handle case where profile doesn't exist yet (PGRST116 error)
      if (error) {
        if (error?.code === 'PGRST116') {
          // Profile doesn't exist yet, return null
          return null;
        }
        throw error;
      }

      // Transform database snake_case to camelCase for React
      return {
        id: data?.id,
        username: data?.username || '',
        firstName: data?.first_name || '',
        lastName: data?.last_name || '',
        email: data?.email || '',
        fullName: data?.full_name || '',
        reflectionVisibility: data?.reflection_visibility || 'private',
        notificationPreferences: data?.notification_preferences || {
          dailyReminders: true,
          studyUpdates: false,
          communityActivity: true
        },
        reminderSchedule: data?.reminder_schedule || '08:00',
        memorization: {
          phase1Days: data?.phase_1_days || 30,
          dailyReviewDays: data?.daily_review_days || 60,
          weeklyReviewWeeks: data?.weekly_review_weeks || 6,
          monthlyReviewMonths: data?.monthly_review_months || 3,
          addToMemorizationIntervalDays: data?.verse_addition_interval_days || 7
        },
        avatar: {
          config: data?.avatar_config || {},
          seed: data?.avatar_seed || '',
          style: data?.avatar_style || 'open-peeps',
          svg: data?.avatar_svg || '',
          svgUrl: data?.avatar_svg_url || '',
          storagePath: data?.avatar_storage_path || ''
        },
        stats: {
          versesMemorized: data?.total_verses_memorized || 0,
          studyStreak: data?.streak_count || 0
        },
        languagePreference: data?.language_preference || 'en',
        timezone: data?.timezone || 'America/New_York',
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// VERSE MANAGEMENT SERVICES (ENHANCED WITH SPACED REPETITION)
// =============================================================================

export const verseService = {
  // Enhanced method for spaced repetition review
  async updateVerseSpacedRepetition(verseId, reviewSuccess) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.rpc('update_verse_spaced_repetition', {
        verse_uuid: verseId,
        review_success: reviewSuccess,
        user_uuid: user?.id
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Network error. Please try again.' } };
    }
  },

  // Fetch actual verse text from bible_verses table
  async getBibleVerseText(bibleVersion, book, chapter, verseStart, verseEnd) {
    try {
      if (!bibleVersion || !book || !chapter || !verseStart) {
        console.error('Missing required parameters for getBibleVerseText:', { bibleVersion, book, chapter, verseStart });
        return null;
      }

      // Get version ID
      const { data: versionData, error: versionError } = await supabase
        ?.from('bible_versions')
        ?.select('id')
        ?.eq('short_name', bibleVersion?.toUpperCase())
        ?.maybeSingle();

      if (versionError || !versionData) {
        console.error('Version not found:', bibleVersion, versionError);
        return null;
      }

      // Get book ID first
      const { data: bookData, error: bookError } = await supabase
        ?.from('bible_books')
        ?.select('id')
        ?.eq('name', book)
        ?.maybeSingle();

      if (bookError || !bookData) {
        console.error('Book not found:', book, bookError);
        return null;
      }

      // Get chapter ID using book_id
      const { data: chapterData, error: chapterError } = await supabase
        ?.from('bible_chapters')
        ?.select('id')
        ?.eq('book_id', bookData?.id)
        ?.eq('chapter_number', chapter)
        ?.maybeSingle();

      if (chapterError || !chapterData) {
        console.error('Chapter not found:', book, chapter, chapterError);
        return null;
      }

      // Fetch verse(s) from bible_verses table
      // Always use range query to support both single verses and verse ranges
      const endVerse = verseEnd || verseStart;
      
      const { data: verses, error: versesError } = await supabase
        ?.from('bible_verses')
        ?.select('text, verse_number')
        ?.eq('version_id', versionData?.id)
        ?.eq('chapter_id', chapterData?.id)
        ?.gte('verse_number', verseStart)
        ?.lte('verse_number', endVerse)
        ?.order('verse_number');

      if (versesError) {
        console.error('Error fetching verses:', versesError);
        return null;
      }

      if (!verses || verses?.length === 0) {
        console.error('Verses not not found:', { book, chapter, verseStart, verseEnd, versionId: versionData?.id, chapterId: chapterData?.id });
        return null;
      }

      // Combine multiple verses if it's a passage
      const combinedText = verses?.map(v => v?.text)?.join(' ');
      console.log('Successfully fetched verse text:', { reference: `${book} ${chapter}:${verseStart}${verseEnd && verseEnd !== verseStart ? `-${verseEnd}` : ''}`, verseCount: verses?.length, textLength: combinedText?.length });
      return combinedText;
    } catch (error) {
      console.error('Error fetching bible verse text:', error);
      return null;
    }
  },

  // Get verses due for spaced repetition review
  async getVersesForSpacedRepetitionReview(userId, reviewDate = null) {
    try {
      const { data, error } = await supabase?.rpc('get_verses_for_spaced_repetition_review', {
        user_uuid: userId,
        review_date: reviewDate || new Date()?.toISOString()?.split('T')?.[0]
      });

      if (error) throw error;

      // Database function now returns text directly - no need for separate queries
      const versesWithText = (data || [])?.map((verse) => ({
        id: verse?.verse_id,
        reference: verse?.reference,
        text: verse?.text, // Text now comes directly from database
        currentPhase: verse?.current_phase,
        successfulReviewCount: verse?.successful_review_count,
        currentInterval: verse?.current_interval,
        daysOverdue: verse?.days_overdue,
        priorityScore: verse?.priority_score,
        isOverdue: verse?.days_overdue > 0,
        bibleVersion: verse?.bible_version,
        book: verse?.book,
        chapter: verse?.chapter,
        verseStart: verse?.verse_start,
        verseEnd: verse?.verse_end,
        verseCount: verse?.verse_count || 1
      }));

      return { data: versesWithText, error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Network error. Please try again.' } };
    }
  },

  // Get user memorization statistics with phase breakdown
  async getUserMemorizationStats(userId) {
    try {
      const { data, error } = await supabase?.rpc('get_user_memorization_stats', {
        user_uuid: userId
      });

      if (error) throw error;

      // Transform data to camelCase for React
      const stats = data?.[0] ? {
        totalVerses: data?.[0]?.total_verses || 0,
        phase1Count: data?.[0]?.phase_1_count || 0,
        phase2Count: data?.[0]?.phase_2_count || 0,
        phase3Count: data?.[0]?.phase_3_count || 0,
        phase4Count: data?.[0]?.phase_4_count || 0,
        phase5Count: data?.[0]?.phase_5_count || 0,
        phase6Count: data?.[0]?.phase_6_count || 0,
        phase7Count: data?.[0]?.phase_7_count || 0,
        versesDueToday: data?.[0]?.verses_due_today || 0,
        versesOverdue: data?.[0]?.verses_overdue || 0,
        averageInterval: data?.[0]?.average_interval || 0,
        totalReviews: data?.[0]?.total_reviews || 0,
        successRate: data?.[0]?.success_rate || 0
      } : {
        totalVerses: 0,
        phase1Count: 0,
        phase2Count: 0,
        phase3Count: 0,
        phase4Count: 0,
        phase5Count: 0,
        phase6Count: 0,
        phase7Count: 0,
        versesDueToday: 0,
        versesOverdue: 0,
        averageInterval: 0,
        totalReviews: 0,
        successRate: 0
      };

      return { data: stats, error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Network error. Please try again.' } };
    }
  },

  // Enhanced getUserVerses to include spaced repetition data
  async getUserVerses(userId, filters = {}) {
    try {
      // Use optimized database function for single-query fetch
      const { data, error } = await supabase?.rpc('get_user_verses_with_text', { user_uuid: userId });

      if (error) throw error;

      // Apply client-side filters (database function returns all verses)
      let filteredData = data || [];

      if (filters?.version && filters?.version !== 'all') {
        filteredData = filteredData?.filter(v => v?.bible_version === filters?.version);
      }
      if (filters?.phase && filters?.phase !== 'all') {
        filteredData = filteredData?.filter(v => v?.current_phase === parseInt(filters?.phase));
      }

      // Apply client-side sorting
      if (filters?.sortBy) {
        switch (filters?.sortBy) {
          case 'reference':
            filteredData = filteredData?.sort((a, b) => a?.reference?.localeCompare(b?.reference));
            break;
          case 'phase':
            filteredData = filteredData?.sort((a, b) => (b?.current_phase || 0) - (a?.current_phase || 0));
            break;
          case 'interval':
            filteredData = filteredData?.sort((a, b) => (b?.current_interval || 0) - (a?.current_interval || 0));
            break;
          case 'nextReview':
            filteredData = filteredData?.sort((a, b) => {
              const dateA = a?.next_review_date ? new Date(a?.next_review_date) : new Date('9999-12-31');
              const dateB = b?.next_review_date ? new Date(b?.next_review_date) : new Date('9999-12-31');
              return dateA - dateB;
            });
            break;
          case 'dateAdded':
          default:
            filteredData = filteredData?.sort((a, b) => {
              const dateA = new Date(a?.created_at);
              const dateB = new Date(b?.created_at);
              return dateB - dateA;
            });
            break;
        }
      }

      // Transform data to match expected format
      const versesWithText = filteredData?.map((verse) => {
        // Calculate progress based on cumulative reviews across phases
        // Phase requirements: 1→2(30), 2→3(10), 3→4(6), 4→5(3), 5→6(3), 6→7(2), 7→mature(16) = 70 total
        // These are reviews completed BEFORE entering each phase (so phase 1 starts at 0%)
        const phaseReviewsCompleted = {
          0: 0,   // Future: 0%
          1: 0,   // Phase 1: starts at 0% (no prior reviews)
          2: 30,  // Phase 2: completed 30 reviews (phase 1 done)
          3: 40,  // Phase 3: 30 + 10
          4: 46,  // Phase 4: 30 + 10 + 6
          5: 49,  // Phase 5: 30 + 10 + 6 + 3
          6: 52,  // Phase 6: 30 + 10 + 6 + 3 + 3
          7: 54,  // Phase 7: 30 + 10 + 6 + 3 + 3 + 2
          8: 70   // Mastered (100%)
        };

        const currentPhase = verse?.current_phase ?? 0;
        const successfulReviewsInPhase = verse?.successful_reviews_in_phase || 0;
        const totalReviewsCompleted = (phaseReviewsCompleted?.[currentPhase] || 0) + successfulReviewsInPhase;
        const progressPercentage = Math.min(100, Math.round((totalReviewsCompleted / 70) * 100));

        return {
          id: verse?.id,
          reference: verse?.reference,
          text: verse?.verse_text || 'Text not available',
          version: verse?.bible_version?.toUpperCase(),
          reviewCount: verse?.times_reviewed || 0,
          tags: verse?.tags || [],
          notes: verse?.notes || '',
          dateAdded: verse?.date_added?.split('T')?.[0],
          lastStudied: verse?.last_studied ? formatRelativeTime(new Date(verse?.last_studied)) : 'Never',
          isInLibrary: true,
          hasNotes: Boolean(verse?.notes),
          hasLinks: false,
          // Passage-specific fields
          bibleVersion: verse?.bible_version,
          book: verse?.book,
          chapter: verse?.chapter,
          verseStart: verse?.verse_start,
          verseEnd: verse?.verse_end,
          verseCount: verse?.verse_count || 1,
          isPassage: verse?.verse_start !== verse?.verse_end,
          // Enhanced spaced repetition fields
          currentPhase: verse?.current_phase ?? 1,
          current_phase: verse?.current_phase ?? 1, // backward compatibility
          successfulReviewsInPhase: verse?.successful_reviews_in_phase || 0,
          successful_reviews_in_phase: verse?.successful_reviews_in_phase || 0, // backward compatibility
          successfulReviewCount: verse?.successful_review_count || 0,
          totalMisses: verse?.total_misses || 0,
          lastMissedDate: verse?.last_missed_date,
          highestIntervalAchieved: verse?.highest_interval_achieved || 1.0,
          currentInterval: verse?.current_interval ?? null,
          nextReviewDate: verse?.next_review_date,
          daysOverdue: verse?.days_overdue,
          verseCount: verse?.verse_count,
          lastReviewedAt: verse?.last_reviewed_at || null,
          last_reviewed_at: verse?.last_reviewed_at || null,
        };
      });

      return { data: versesWithText, error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Network error. Please try again.' } };
    }
  },

  async createVerse(verseData) {
    try {
      // Parse reference to extract book, chapter, verse_start, verse_end
      if (verseData?.reference && (!verseData?.book || !verseData?.chapter || !verseData?.verse_start)) {
        const referenceMatch = verseData?.reference?.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/);
        if (referenceMatch) {
          verseData.book = referenceMatch?.[1]?.trim();
          verseData.chapter = parseInt(referenceMatch?.[2]);
          verseData.verse_start = parseInt(referenceMatch?.[3]);
          verseData.verse_end = referenceMatch?.[4] ? parseInt(referenceMatch?.[4]) : null;
        }
      }

      // Ensure bible_version is set (default to KJV if not provided)
      if (!verseData?.bible_version) {
        verseData.bible_version = 'kjv';
      }

      // If text not provided, try to get it from Bible database
      if (!verseData?.text && verseData?.book && verseData?.chapter && verseData?.verse_start) {
        const { data: verseTexts, error: fetchError } = await bibleService?.getVerseRangeText(
          verseData?.book,
          verseData?.chapter,
          verseData?.verse_start,
          verseData?.verse_end || verseData?.verse_start,
          verseData?.bible_version
        );

        if (!fetchError && verseTexts && verseTexts?.length > 0) {
          // If it's a single verse, use the text directly
          if (verseTexts?.length === 1) {
            verseData.text = verseTexts?.[0]?.text;
          } else {
            // If it's a range, concatenate the texts
            verseData.text = verseTexts?.map(v => v?.text)?.join(' ');
          }
        }
      }

      // If still no text, set a placeholder
      if (!verseData?.text) {
        verseData.text = 'Text to be added later';
      }

      // Ensure newly added verses have next_review_date set to today (date added)
      if (!verseData?.next_review_date && verseData?.current_phase !== 0) {
        verseData.next_review_date = new Date()?.toISOString()?.split('T')?.[0];
      }

      // Set spaced repetition defaults for new verses
      if (!verseData?.current_phase && verseData?.current_phase !== 0) {
        verseData.current_phase = 1;
      }
      if (!verseData?.current_interval && verseData?.current_phase !== 0) {
        verseData.current_interval = 1.0;
      }

      // Calculate verse_count if not provided
      if (!verseData?.verse_count) {
        if (verseData?.verse_end && verseData?.verse_start) {
          verseData.verse_count = verseData?.verse_end - verseData?.verse_start + 1;
        } else {
          verseData.verse_count = 1;
        }
      }

      const { data, error } = await supabase
        ?.from('verses')
        ?.insert(verseData)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Enhanced createVerseFromBible - directly from Bible database
  async createVerseFromBible(userId, reference, additionalData = {}) {
    try {
      // Get verse from Bible database
      const { data: bibleVerse, error: bibleError } = await supabase
        ?.rpc('get_verse_by_reference', {
          verse_reference: reference
        });

      if (bibleError || !bibleVerse?.[0]) {
        return { error: { message: `Verse "${reference}" not found in Bible database` } };
      }

      let verse = bibleVerse?.[0];
      
      // Create user verse from Bible data
      const todayDate = new Date()?.toISOString()?.split('T')?.[0];
      const verseData = {
        user_id: userId,
        reference: verse?.reference,
        text: verse?.text,
        book: verse?.book_name,
        chapter: verse?.chapter_number,
        verse_start: verse?.verse_number,
        bible_version: 'kjv',
        // Spaced repetition defaults - set next_review_date to today (date added)
        current_phase: 1,
        current_interval: 1.0,
        next_review_date: todayDate,
        ...additionalData
      };

      const { data, error } = await supabase
        ?.from('verses')
        ?.insert(verseData)
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Enhanced createVerseFromBibleText - create verse directly from Bible Reader text
  async createVerseFromBibleText(userId, book, chapter, verseStart, verseEnd, text, reference, version) {
    try {
      // Map version string to bible_version enum
      const versionMap = {
        'kjv': 'kjv',
        'asv': 'niv', // Map to available enum value
        'niv': 'niv',
        'esv': 'esv',
        'nkjv': 'nkjv',
        'nasb': 'nasb',
        'nlt': 'nlt'
      };

      const todayDate = new Date()?.toISOString()?.split('T')?.[0];
      const verseData = {
        user_id: userId,
        reference: reference,
        text: text,
        book: book,
        chapter: chapter,
        verse_start: verseStart,
        verse_end: verseStart === verseEnd ? null : verseEnd,
        bible_version: versionMap?.[version?.toLowerCase()] || 'niv',
        // Spaced repetition defaults - set next_review_date to today (date added)
        current_phase: 1,
        current_interval: 1.0,
        next_review_date: todayDate
      };

      const { data, error } = await supabase
        ?.from('verses')
        ?.insert(verseData)
        ?.select()
        ?.single();

      if (error) {
        // Handle duplicate verse error
        if (error?.code === '23505') {
          return { error: { message: 'This verse is already in your library.' } };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating verse from Bible text:', error);
      return { error: { message: error?.message || 'Network error. Please try again.' } };
    }
  },

  // Enhanced updateVerse with spaced repetition parameter support
  async updateVerse(verseId, updates) {
    try {
      // Handle spaced repetition updates
      if (updates?.current_phase || updates?.review_count !== undefined) {
        // If phase is being changed, we might want to recalculate interval
        if (updates?.current_phase) {
          // Calculate new interval based on phase
          const { data: intervalData, error: intervalError } = await supabase?.rpc('calculate_spaced_repetition_interval', {
            phase: updates?.current_phase,
            consecutive_successes: 0 // Reset when manually changing phase
          });

          if (!intervalError && intervalData) {
            updates.current_interval = intervalData;
            updates.next_review_date = new Date(Date?.now() + (intervalData * 24 * 60 * 60 * 1000))?.toISOString()?.split('T')?.[0];
          }
        }
      }

      const { data, error } = await supabase
        ?.from('verses')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', verseId)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get verses due for review (with grace period support)
  async getVersesDueForReview(userId, reviewDate = null) {
    try {
      if (!userId) {
        return { data: [], error: { message: 'User ID is required.' } };
      }

      const dateParam = reviewDate || new Date()?.toISOString()?.split('T')?.[0];

      const { data, error } = await supabase?.rpc('get_verses_due_for_review', {
        p_user_id: userId,
        p_review_date: dateParam
      });

      if (error) {
        console.error('Error fetching verses due for review:', error);
        return { data: [], error };
      }

      // Transform to camelCase
      const transformedData = (data || [])?.map(verse => ({
        verseId: verse?.verse_id,
        id: verse?.verse_id,
        reference: verse?.reference,
        text: verse?.text,
        currentPhase: verse?.current_phase,
        successfulReviewsInPhase: verse?.successful_reviews_in_phase,
        consecutiveSuccessfulReviews: verse?.consecutive_successful_reviews,
        nextReviewDate: verse?.next_review_date,
        daysOverdue: verse?.days_overdue,
        isInGracePeriod: verse?.is_in_grace_period,
        verseCount: verse?.verse_count
      }));

      return { data: transformedData, error: null };
    } catch (err) {
      console.error('Network error fetching verses due for review:', err);
      return { data: [], error: { message: 'Network error. Please try again.' } };
    }
  },

  // Enhanced deleteVerse with proper cascade handling
  async deleteVerse(verseId) {
    try {
      // Note: Foreign key constraints should handle cascading deletes for related data
      // (verse_reflections, user_activities, etc.)
      const { data, error } = await supabase
        ?.from('verses')
        ?.delete()
        ?.eq('id', verseId);
      
      if (error) {
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Network error. Please try again.' } };
    }
  },

  // New method for bulk verse operations
  async bulkUpdateVerses(verseIds, updates) {
    try {
      const { data, error } = await supabase
        ?.from('verses')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.in('id', verseIds)
        ?.select();
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // New method for bulk verse deletion
  async bulkDeleteVerses(verseIds) {
    try {
      const { data, error } = await supabase
        ?.from('verses')
        ?.delete()
        ?.in('id', verseIds);
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Enhanced method to get spaced repetition suggestions based on performance
  async getSpacedRepetitionSuggestions(userId) {
    try {
      const { data, error } = await supabase
        ?.from('verses')
        ?.select(`
          id,
          reference,
          current_phase,
          current_interval,
          total_misses,
          successful_review_count,
          next_review_date,
          last_reviewed_at
        `)
        ?.eq('user_id', userId)
        ?.order('next_review_date');

      if (error) throw error;

      // Analyze and provide suggestions
      const suggestions = data?.map(verse => {
        let suggestion = null;
        const daysSinceReview = verse?.last_reviewed_at ? 
          Math.floor((new Date() - new Date(verse?.last_reviewed_at)) / (1000 * 60 * 60 * 24)) : 0;
        
        // Suggest phase adjustments based on performance
        if (verse?.total_misses > verse?.successful_review_count && verse?.current_phase > 1) {
          suggestion = {
            type: 'phase_reduction',
            message: `Consider reducing ${verse?.reference} to Phase ${verse?.current_phase - 1} due to difficulty`,
            suggestedPhase: verse?.current_phase - 1
          };
        } else if (verse?.successful_review_count > verse?.current_phase * 3 && verse?.current_phase < 7) {
          suggestion = {
            type: 'phase_advancement',
            message: `${verse?.reference} may be ready for Phase ${verse?.current_phase + 1}`,
            suggestedPhase: verse?.current_phase + 1
          };
        } else if (daysSinceReview > 30 && verse?.current_phase > 2) {
          suggestion = {
            type: 'review_overdue',
            message: `${verse?.reference} has not been reviewed for ${daysSinceReview} days`,
            suggestedAction: 'schedule_review'
          };
        }

        return {
          verse,
          suggestion
        };
      })?.filter(item => item?.suggestion !== null) || [];

      return { data: suggestions, error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Network error. Please try again.' } };
    }
  },

  // Method to reset verse spaced repetition progress
  async resetVerseProgress(verseId, resetToPhase = 1) {
    try {
      const updates = {
        current_phase: resetToPhase,
        current_interval: 1.0,
        successful_review_count: 0,
        total_misses: 0,
        next_review_date: new Date()?.toISOString()?.split('T')?.[0],
        last_reviewed_at: null,
        last_missed_date: null
      };

      const { data, error } = await supabase
        ?.from('verses')
        ?.update(updates)
        ?.eq('id', verseId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  // Start memorization for a future verse (Phase 0 → Phase 1)
  async startVerseMemorization(verseId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.rpc('start_verse_memorization', {
        verse_uuid: verseId,
        user_uuid: user?.id
      });

      if (error) throw error;

      // After RPC sets phase=1, use scheduleVerse to spread new verses across
      // today/tomorrow (windowMinDay=0, windowMaxDay=1) instead of all landing on CURRENT_DATE.
      // For a newly activated Phase 1 verse (no lastReviewedAt), scheduleVerse
      // automatically uses windowMinDay=0, windowMaxDay=1 via the isUnreviewed path.
      await schedulerService?.scheduleVerse(verseId, 1, null, user?.id);

      return data;
    } catch (error) {
      console.error('Error starting verse memorization:', error);
      throw error;
    }
  },

  // Move verse back to future (Phase X → Phase 0)
  async moveVerseToFuture(verseId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.rpc('move_verse_to_future', {
        verse_uuid: verseId,
        user_uuid: user?.id
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error moving verse to future:', error);
      throw error;
    }
  },

  // Get future verses (Phase 0)
  async getFutureVerses() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.rpc('get_future_verses', {
        user_uuid: user?.id
      });

      if (error) throw error;

      return data?.map(verse => ({
        id: verse?.verse_id,
        reference: verse?.reference,
        text: verse?.text,
        createdAt: verse?.created_at,
        tags: verse?.tags || []
      })) || [];
    } catch (error) {
      console.error('Error fetching future verses:', error);
      throw error;
    }
  },

  // Get verse statistics by phase
  async getVerseStatisticsByPhase(userId) {
    try {
      const { data, error } = await supabase?.rpc('get_verse_statistics_by_phase', {
        user_uuid: userId
      });

      if (error) throw error;
      
      return {
        futureVerses: data?.future_verses || 0,
        phase1Verses: data?.phase_1_verses || 0,
        phase2Verses: data?.phase_2_verses || 0,
        phase3Verses: data?.phase_3_verses || 0,
        phase4Verses: data?.phase_4_verses || 0,
        phase5Verses: data?.phase_5_verses || 0,
        phase6Verses: data?.phase_6_verses || 0,
        phase7Verses: data?.phase_7_verses || 0,
        totalActiveVerses: data?.total_active_verses || 0,
        totalVerses: data?.total_verses || 0,
        versesDueToday: data?.verses_due_today || 0,
        overdueVerses: data?.overdue_verses || 0
      };
    } catch (error) {
      console.error('Error fetching verse statistics by phase:', error);
      throw error;
    }
  },

  // Enhanced getVersesForReview to work with phase-based logic
  async getVersesForReview(reviewDate = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.rpc('get_verses_for_spaced_repetition_review', {
        user_uuid: user?.id,
        review_date: reviewDate || new Date()?.toISOString()?.split('T')?.[0]
      });

      if (error) throw error;

      return data?.map(verse => ({
        id: verse?.verse_id,
        reference: verse?.reference,
        text: verse?.text,
        currentPhase: verse?.current_phase,
        successfulReviewCount: verse?.successful_review_count,
        currentInterval: verse?.current_interval,
        daysOverdue: verse?.days_overdue,
        priorityScore: verse?.priority_score
      })) || [];
    } catch (error) {
      console.error('Error fetching verses for review:', error);
      throw error;
    }
  },

  // Update verse to use enhanced phase management
  async updateVersePhase(verseId, newPhase, reviewCount) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert phase logic: Phase 0 means future (null next_review_date)
      const updates = {
        current_phase: newPhase,
        review_count: reviewCount || 0,
        updated_at: new Date()?.toISOString()
      };

      if (newPhase === 0) {
        // Phase 0 = Future verse, no next review date
        updates.next_review_date = null;
        updates.status = 'new';
      } else {
        // Phase 1-7 = Active memorization, set next review date to today
        updates.next_review_date = new Date()?.toISOString()?.split('T')?.[0];
        updates.status = newPhase >= 3 ? 'learning' : 'new';
      }

      const { data, error } = await supabase?.from('verses')?.update(updates)?.eq('id', verseId)?.eq('user_id', user?.id)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating verse phase:', error);
      throw error;
    }
  },

  // NEW: Comprehensive review response handler with phase logic and scheduling
  async handleReviewResponse(verseId, response, sessionType = 'daily_review', verseData = null, sharedScheduleMap = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use provided verse data or fetch it
      let verse = verseData;
      if (!verse) {
        const { data: fetchedVerse, error: fetchError } = await supabase
          ?.from('verses')
          ?.select('*')
          ?.eq('id', verseId)
          ?.single();
        if (fetchError || !fetchedVerse) throw new Error('Verse not found');
        verse = fetchedVerse;
      }

      // Phase advancement requirements (successful reviews needed to advance)
      const PHASE_REQUIREMENTS = {
        0: 1,   // Phase 0 -> 1: 1 successful review
        1: 30,  // Phase 1 -> 2: 30 successful reviews
        2: 10,  // Phase 2 -> 3: 10 successful reviews
        3: 6,   // Phase 3 -> 4: 6 successful reviews
        4: 3,   // Phase 4 -> 5: 3 successful reviews
        5: 3,   // Phase 5 -> 6: 3 successful reviews
        6: 2,   // Phase 6 -> 7: 2 successful reviews
        7: 16,  // Phase 7 -> Mature: 16 successful reviews (4 years)
      };

      // Initialize update object
      const updates = {
        last_reviewed_at: new Date()?.toISOString(),
        review_count: (verse?.review_count || 0) + 1,
      };

      // Track phase change for history
      let phaseChanged = false;
      let phaseBefore = verse?.current_phase;
      let phaseAfter = verse?.current_phase;
      let nextReviewDateBefore = verse?.next_review_date;

      // Handle response logic
      if (response === 'good') {
        // Increment counters
        updates.successful_review_count = (verse?.successful_review_count || 0) + 1;
        updates.successful_reviews_in_phase = (verse?.successful_reviews_in_phase || 0) + 1;
        updates.consecutive_successful_reviews = (verse?.consecutive_successful_reviews || 0) + 1;

        // Check if phase advancement criteria met
        const currentPhase = verse?.current_phase || 0;
        const requiredReviews = PHASE_REQUIREMENTS?.[currentPhase];
        
        if (updates?.successful_reviews_in_phase >= requiredReviews) {
          // Advance phase
          if (currentPhase === 0) {
            updates.current_phase = 1;
          } else if (currentPhase < 7) {
            updates.current_phase = currentPhase + 1;
          } else if (currentPhase === 7) {
            updates.current_phase = 8; // Mature phase
          }
          
          // Reset phase counters on advancement
          updates.successful_reviews_in_phase = 0;
          updates.phase_start_date = new Date()?.toISOString()?.split('T')?.[0];
          
          phaseChanged = true;
          phaseAfter = updates?.current_phase;
        }
      } else if (response === 'hard') {
        // Hard: Still successful but difficult
        updates.successful_review_count = (verse?.successful_review_count || 0) + 1;
        updates.successful_reviews_in_phase = (verse?.successful_reviews_in_phase || 0) + 1;
        updates.consecutive_successful_reviews = (verse?.consecutive_successful_reviews || 0) + 1;
        // No phase change for 'hard'
      } else if (response === 'missed') {
        // Missed: Apply penalties
        updates.total_misses = (verse?.total_misses || 0) + 1;
        updates.last_missed_date = new Date()?.toISOString()?.split('T')?.[0];
        updates.consecutive_successful_reviews = 0; // Reset consecutive

        // Phase downgrade logic (from user's existing implementation)
        const currentPhase = verse?.current_phase || 1;
        
        if (currentPhase === 1) {
          // Phase 1: Subtract 10 reviews or reset to 0
          const currentSuccessful = verse?.successful_reviews_in_phase || 0;
          if (currentSuccessful < 10) {
            updates.successful_reviews_in_phase = 0;
          } else {
            updates.successful_reviews_in_phase = currentSuccessful - 10;
          }
        } else if (currentPhase === 2) {
          // Phase 2: Reset to 0, stay in Phase 2
          updates.successful_reviews_in_phase = 0;
        } else if (currentPhase >= 3 && currentPhase <= 7) {
          // Phase 3-7: Reset to 0, demote to previous phase
          updates.current_phase = currentPhase - 1;
          updates.successful_reviews_in_phase = 0;
          updates.phase_start_date = new Date()?.toISOString()?.split('T')?.[0];
          phaseChanged = true;
          phaseAfter = updates?.current_phase;
        } else if (currentPhase === 8) {
          // Mature: Reset to 0, demote to Phase 7
          updates.current_phase = 7;
          updates.successful_reviews_in_phase = 0;
          updates.phase_start_date = new Date()?.toISOString()?.split('T')?.[0];
          phaseChanged = true;
          phaseAfter = updates?.current_phase;
        }
      }

      // Update verse in database
      const { error: updateError } = await supabase
        ?.from('verses')
        ?.update(updates)
        ?.eq('id', verseId);

      if (updateError) throw updateError;

      // Call scheduler service to update next_review_date
      const scheduleResult = await schedulerService?.scheduleVerse(
        verseId,
        updates?.current_phase || verse?.current_phase,
        updates?.last_reviewed_at,
        user?.id,
        sharedScheduleMap // Pass shared map to avoid redundant fetches
      );

      const nextReviewDateAfter = scheduleResult?.nextReviewDate || verse?.next_review_date;

      // Log to review_history
      const historyEntry = {
        user_id: user?.id,
        verse_id: verseId,
        response,
        was_successful: response === 'good' || response === 'hard',
        phase_at_review: verse?.current_phase,
        successful_reviews_in_phase_before: verse?.successful_reviews_in_phase || 0,
        consecutive_successful_before: verse?.consecutive_successful_reviews || 0,
        next_review_date_before: nextReviewDateBefore,
        next_review_date_after: nextReviewDateAfter,
        phase_changed: phaseChanged,
        phase_before: phaseBefore,
        phase_after: phaseAfter,
        was_overdue: verse?.next_review_date ? new Date(verse.next_review_date) < new Date() : false,
        days_overdue: verse?.next_review_date ? Math.max(0, Math.floor((new Date() - new Date(verse.next_review_date)) / (1000 * 60 * 60 * 24))) : 0,
        session_type: sessionType,
        reviewed_at: new Date()?.toISOString(),
      };

      const { error: historyError } = await supabase
        ?.from('review_history')
        ?.insert(historyEntry);

      if (historyError) {
        console.error('Error logging review history:', historyError);
        // Don't fail the entire operation if history logging fails
      }

      return {
        success: true,
        phaseChanged,
        phaseBefore,
        phaseAfter,
        nextReviewDate: nextReviewDateAfter,
        error: null,
      };
    } catch (error) {
      console.error('Error handling review response:', error);
      return { success: false, error: { message: error?.message || 'Failed to process review response' } };
    }
  },

  // OPTIMIZED: Batch process all review responses with shared schedule map and pre-fetched verse data
  async batchHandleReviewResponsesOptimized(reviewUpdates, sessionType = 'daily_review') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (!reviewUpdates || reviewUpdates?.length === 0) {
        return { successful: 0, failed: 0, results: [] };
      }

      // Step 1: Fetch all verse data in a single query
      const verseIds = reviewUpdates?.map(u => u?.verseId);
      const { data: allVerses, error: fetchError } = await supabase
        ?.from('verses')
        ?.select('*')
        ?.in('id', verseIds);

      if (fetchError) throw fetchError;

      const verseMap = {};
      allVerses?.forEach(v => { verseMap[v?.id] = v; });

      // Step 2: Fetch shared schedule map once
      const sharedScheduleMap = await schedulerService?.getScheduleMap(user?.id);

      // Step 3: Process all updates in parallel using shared data
      const results = await Promise.allSettled(
        reviewUpdates?.map(update =>
          this.handleReviewResponse(
            update?.verseId,
            update?.response,
            sessionType,
            verseMap?.[update?.verseId] || null,
            sharedScheduleMap
          )
        )
      );

      const successfulUpdates = results?.filter(r => r?.status === 'fulfilled' && r?.value?.success)?.length;
      const failedUpdates = results?.filter(r => r?.status === 'rejected' || !r?.value?.success)?.length;

      return {
        successful: successfulUpdates,
        failed: failedUpdates,
        results: results?.map((r, i) => ({
          verseId: reviewUpdates?.[i]?.verseId,
          ...(r?.status === 'fulfilled' ? r?.value : { success: false, error: r?.reason?.message }),
        })),
      };
    } catch (error) {
      console.error('Error in batchHandleReviewResponsesOptimized:', error);
      return { successful: 0, failed: reviewUpdates?.length, error: error?.message };
    }
  },

  // NEW: Batch process multiple review responses (optimized for end-of-session processing)
  async batchHandleReviewResponses(reviewUpdates, sessionType = 'daily_review') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (!reviewUpdates || reviewUpdates?.length === 0) {
        return { success: true, results: [] };
      }

      // Process all updates in parallel
      const results = await Promise.allSettled(
        reviewUpdates?.map(update => 
          this.handleReviewResponse(update?.verseId, update?.response, sessionType)
        )
      );

      // Aggregate results
      const successfulUpdates = results?.filter(r => r?.status === 'fulfilled')?.length;
      const failedUpdates = results?.filter(r => r?.status === 'rejected')?.length;

      return {
        success: failedUpdates === 0,
        successfulUpdates,
        failedUpdates,
        results: results?.map((r, idx) => ({
          verseId: reviewUpdates?.[idx]?.verseId,
          status: r?.status,
          data: r?.status === 'fulfilled' ? r?.value : null,
          error: r?.status === 'rejected' ? r?.reason?.message : null
        }))
      };
    } catch (error) {
      console.error('Error in batch review processing:', error);
      return {
        success: false,
        error: error?.message || 'Batch processing failed'
      };
    }
  }
};

// Helper function to format relative time
const formatRelativeTime = (date) => {
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
  
  if (diffInHours < 24) {
    return diffInHours <= 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  } else {
    const diffInDays = Math.floor(diffInHours / 24);
    return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
  }
};

// =============================================================================
// REFLECTION SERVICES
// =============================================================================

export const reflectionService = {
  async createReflection(reflectionData) {
    try {
      const { data, error } = await supabase
        ?.from('verse_reflections')
        ?.insert(reflectionData)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async getUserReflections(userId, isPrivate = null) {
    try {
      let query = supabase
        ?.from('verse_reflections')
        ?.select(`
          *,
          verses (
            reference,
            text,
            bible_version
          )
        `)
        ?.eq('user_id', userId);

      if (isPrivate !== null) {
        query = query?.eq('is_private', isPrivate);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// PROGRESS TRACKING SERVICES
// =============================================================================

export const progressService = {
  async getUserProgress(userId, dateRange = '7days') {
    try {
      const endDate = new Date();
      const startDate = new Date();
      
      switch (dateRange) {
        case '1days':
          startDate?.setDate(endDate?.getDate() - 1);
          break;
        case '7days':
          startDate?.setDate(endDate?.getDate() - 7);
          break;
        case '30days':
          startDate?.setDate(endDate?.getDate() - 30);
          break;
        case '90days':
          startDate?.setDate(endDate?.getDate() - 90);
          break;
        default:
          startDate?.setDate(endDate?.getDate() - 7);
      }

      const { data, error } = await supabase
        ?.from('user_progress')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.gte('date', startDate?.toISOString()?.split('T')?.[0])
        ?.lte('date', endDate?.toISOString()?.split('T')?.[0])
        ?.order('date', { ascending: false });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async recordDailyProgress(userId, progressData) {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      
      const { data, error } = await supabase
        ?.from('user_progress')
        ?.upsert({
          user_id: userId,
          date: today,
          verses_studied: progressData?.versesStudied || 0,
          verses_reviewed: progressData?.versesReviewed || 0,
          study_time_minutes: progressData?.studyTimeMinutes || 0,
          streak_maintained: progressData?.streakMaintained || false,
          daily_goal_met: progressData?.dailyGoalMet || false
        })
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async getProgressStats(userId) {
    try {
      // Get streak info from new streak service
      const { data: streakInfo, error: streakError } = await supabase
        ?.rpc('get_user_streak_info', {
          p_user_id: userId
        });

      if (streakError) {
        console.warn('Streak info query error:', streakError);
      }

      // Get user profile for basic stats - use maybeSingle() instead of single()
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('total_verses_memorized')
        ?.eq('id', userId)
        ?.maybeSingle();

      if (profileError) {
        console.warn('Profile query error:', profileError);
      }

      // Get verse counts by status with verse_count for aggregate totals
      const { data: verseCounts, error: verseError } = await supabase
        ?.from('verses')
        ?.select('status, verse_count')
        ?.eq('user_id', userId);

      if (verseError) {
        console.warn('Verses query error:', verseError);
      }

      // Calculate stats with safe defaults - sum verse_count for aggregate totals
      const safeCounts = Array.isArray(verseCounts) ? verseCounts : [];
      const stats = {
        totalMemorized: profile?.total_verses_memorized || 0,
        streakDays: streakInfo?.[0]?.current_streak || 0,
        masteredVerses: safeCounts?.filter(v => v?.status === 'mastered')?.reduce((sum, v) => sum + (v?.verse_count || 1), 0) || 0,
        learningVerses: safeCounts?.filter(v => v?.status === 'learning')?.reduce((sum, v) => sum + (v?.verse_count || 1), 0) || 0,
        newVerses: safeCounts?.filter(v => v?.status === 'new')?.reduce((sum, v) => sum + (v?.verse_count || 1), 0) || 0,
        totalVerses: safeCounts?.reduce((sum, v) => sum + (v?.verse_count || 1), 0) || 0
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Progress stats service error:', error);
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// ACHIEVEMENT SERVICES
// =============================================================================

export const achievementService = {
  async getUserAchievements(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_achievements')
        ?.select(`
          *,
          achievements (
            name,
            description,
            achievement_type,
            icon,
            color,
            background_color
          )
        `)
        ?.eq('user_id', userId)
        ?.order('earned_at', { ascending: false });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async checkAndAwardAchievements(userId) {
    try {
      // This would contain logic to check user progress and award achievements
      // For now, just return existing achievements
      return await this.getUserAchievements(userId);
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// LINKED VERSES SERVICES
// =============================================================================

export const linkedVersesService = {
  async getLinkedVerses(verseId) {
    try {
      const { data, error } = await supabase
        ?.from('verse_linked_verses')
        ?.select(`
          id,
          relationship_label,
          linked_verse_id,
          linked_reference,
          linked_text,
          linked_version,
          linked:verses!verse_linked_verses_linked_verse_id_fkey(id, reference, text, bible_version)
        `)
        ?.eq('verse_id', verseId);
      if (error) throw error;
      return { data: (data || [])?.map(row => ({
        id: row?.id,
        linkedVerseId: row?.linked_verse_id,
        // Use library verse data if available, otherwise use stored free-form data
        reference: row?.linked?.reference || row?.linked_reference,
        text: row?.linked?.text || row?.linked_text,
        version: (row?.linked?.bible_version || row?.linked_version || '')?.toUpperCase(),
        relationship: row?.relationship_label || 'Related'
      })), error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message || 'Failed to load linked verses' } };
    }
  },

  async addLinkedVerse(userId, verseId, linkedVerseId, relationshipLabel = 'Related') {
    try {
      const { data, error } = await supabase
        ?.from('verse_linked_verses')
        ?.insert({ user_id: userId, verse_id: verseId, linked_verse_id: linkedVerseId, relationship_label: relationshipLabel })
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to add linked verse' } };
    }
  },

  // New: add a link to any Bible verse by reference/text (not in user's library)
  async addLinkedVerseByReference(userId, verseId, linkedReference, linkedText, linkedVersion, relationshipLabel = 'Related') {
    try {
      const { data, error } = await supabase
        ?.from('verse_linked_verses')
        ?.insert({
          user_id: userId,
          verse_id: verseId,
          linked_verse_id: null,
          linked_reference: linkedReference,
          linked_text: linkedText,
          linked_version: linkedVersion || 'KJV',
          relationship_label: relationshipLabel
        })
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to add linked verse' } };
    }
  },

  async removeLinkedVerse(linkId) {
    try {
      const { error } = await supabase
        ?.from('verse_linked_verses')
        ?.delete()
        ?.eq('id', linkId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Failed to remove linked verse' } };
    }
  }
};

// =============================================================================
// REVIEW HISTORY SERVICES
// =============================================================================

export const reviewHistoryService = {
  async getVerseReviewHistory(verseId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('review_history')
        ?.select('*')
        ?.eq('verse_id', verseId)
        ?.order('reviewed_at', { ascending: false })
        ?.limit(limit);
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message || 'Failed to load review history' } };
    }
  }
};

// =============================================================================
// ACTIVITY SERVICES
// =============================================================================

export const activityService = {
  async getUserActivities(userId, limit = 20) {
    try {
      const { data, error } = await supabase
        ?.from('user_activities')
        ?.select(`
          *,
          verses (
            reference,
            text
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async logActivity(userId, activityData) {
    try {
      const { data, error } = await supabase
        ?.from('user_activities')
        ?.insert({
          user_id: userId,
          verse_id: activityData?.verseId || null,
          activity_type: activityData?.type,
          title: activityData?.title,
          description: activityData?.description || '',
          metadata: activityData?.metadata || {}
        })
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};

// =============================================================================
// READING PLAN SERVICES
// =============================================================================

export const readingPlanService = {
  /**
   * Get all reading plans with their daily readings and references
   */
  async getReadingPlans(category = null, featured = null) {
    try {
      let query = supabase
        ?.from('reading_plans')
        ?.select(`
          *,
          reading_plan_days (
            id,
            day_number,
            title,
            description,
            display_order,
            reading_plan_references (
              id,
              book_name,
              chapter,
              verse_start,
              verse_end,
              reference_text,
              display_order
            )
          )
        `)
        ?.eq('is_active', true)
        ?.eq('is_public', true);

      if (category && category !== 'all') {
        query = query?.eq('category', category);
      }
      if (featured !== null) {
        query = query?.eq('is_featured', featured);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;

      // Fetch enrollment counts for all plans
      const planIds = data?.map(p => p?.id) || [];
      let enrollmentCounts = {};
      let completedCounts = {};
      if (planIds?.length > 0) {
        const { data: enrollData } = await supabase
          ?.from('user_reading_plans')
          ?.select('reading_plan_id, status')
          ?.in('reading_plan_id', planIds);
        
        (enrollData || [])?.forEach(row => {
          enrollmentCounts[row?.reading_plan_id] = (enrollmentCounts?.[row?.reading_plan_id] || 0) + 1;
          if (row?.status === 'completed') {
            completedCounts[row?.reading_plan_id] = (completedCounts?.[row?.reading_plan_id] || 0) + 1;
          }
        });
      }

      // Calculate estimated reading time for each plan
      const plansWithReadingTime = data?.map(plan => {
        let totalVerses = this.calculateTotalVerses(plan);
        const estimatedMinutes = Math.ceil((totalVerses * 26) / 238); // 26 words/verse, 238 WPM
        
        return {
          ...plan,
          estimated_daily_reading_time: Math.ceil(estimatedMinutes / (plan?.duration_days || 1)),
          enrolled_count: enrollmentCounts?.[plan?.id] || 0,
          completed_count: completedCounts?.[plan?.id] || 0,
        };
      });

      return { data: plansWithReadingTime, error: null };
    } catch (error) {
      console.error('Error fetching reading plans:', error);
      return { error: { message: 'Failed to fetch reading plans' } };
    }
  },

  /**
   * Calculate total verses in a reading plan
   */
  calculateTotalVerses(plan) {
    if (!plan?.reading_plan_days) return 0;
    
    let totalVerses = 0;
    plan?.reading_plan_days?.forEach(day => {
      day?.reading_plan_references?.forEach(ref => {
        if (ref?.verse_start && ref?.verse_end) {
          // Specific verse range
          totalVerses += (ref?.verse_end - ref?.verse_start + 1);
        } else if (ref?.verse_start) {
          // Single verse
          totalVerses += 1;
        } else {
          // Entire chapter - estimate 26 verses per chapter (Bible average)
          totalVerses += 26;
        }
      });
    });
    
    return totalVerses;
  },

  /**
   * Get user's enrolled reading plans with progress
   */
  async getUserReadingPlans(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_reading_plans')
        ?.select(`
          *,
          reading_plans (
            id,
            name,
            description,
            duration_days,
            category,
            tags,
            estimated_daily_reading_time
          )
        `)
        ?.eq('user_id', userId)
        ?.order('started_at', { ascending: false });
      
      if (error) throw error;

      // Fetch progress for each enrollment
      const plansWithProgress = await Promise.all(
        data?.map(async (enrollment) => {
          const progress = await this.calculateUserProgress(enrollment?.id);
          const nextReading = await this.getNextReading(enrollment?.id);
          const streakDays = await this.calculateStreak(enrollment?.id);
          
          return {
            ...enrollment,
            progress: progress?.percentage || 0,
            completedReferences: progress?.completed || 0,
            totalReferences: progress?.total || 0,
            nextReading,
            streakDays
          };
        }) || []
      );

      return { data: plansWithProgress, error: null };
    } catch (error) {
      console.error('Error fetching user reading plans:', error);
      return { error: { message: 'Failed to fetch user reading plans' } };
    }
  },

  /**
   * Calculate user's progress for a specific enrollment
   */
  async calculateUserProgress(userReadingPlanId) {
    try {
      // Get user reading plan details
      const { data: planData, error: planError } = await supabase
        ?.from('user_reading_plans')
        ?.select(`
          reading_plan_id,
          reading_plans (
            reading_plan_days (
              reading_plan_references (id)
            )
          )
        `)
        ?.eq('id', userReadingPlanId)
        ?.single();

      if (planError) throw planError;

      let totalReferences = 0;
      planData?.reading_plans?.reading_plan_days?.forEach(day => {
        totalReferences += day?.reading_plan_references?.length || 0;
      });

      // Get completed references
      const { data: progressData, error: progressError } = await supabase
        ?.from('user_reading_progress')
        ?.select('id')
        ?.eq('user_reading_plan_id', userReadingPlanId)
        ?.eq('is_completed', true);

      if (progressError) throw progressError;

      const completedReferences = progressData?.length || 0;
      const percentage = totalReferences > 0 ? (completedReferences / totalReferences) * 100 : 0;

      return {
        total: totalReferences,
        completed: completedReferences,
        percentage: Math.round(percentage * 100) / 100
      };
    } catch (error) {
      console.error('Error calculating progress:', error);
      throw error;
    }
  },

  /**
   * Get next reading for user
   */
  async getNextReading(userReadingPlanId) {
    try {
      const { data: enrollment, error: enrollError } = await supabase
        ?.from('user_reading_plans')
        ?.select('start_date, current_day')
        ?.eq('id', userReadingPlanId)
        ?.single();

      if (enrollError) throw enrollError;

      const currentDay = enrollment?.current_day || 1;

      // Get the next day's reading
      const { data: dayData, error: dayError } = await supabase
        ?.from('user_reading_plans')
        ?.select(`
          reading_plans (
            reading_plan_days (
              day_number,
              title,
              reading_plan_references (
                reference_text,
                display_order
              )
            )
          )
        `)
        ?.eq('id', userReadingPlanId)
        ?.single();

      if (dayError) throw dayError;

      const nextDay = dayData?.reading_plans?.reading_plan_days?.find(
        day => day?.day_number === currentDay
      );

      if (!nextDay) return null;

      const references = nextDay?.reading_plan_references
        ?.sort((a, b) => a?.display_order - b?.display_order)
        ?.map(ref => ref?.reference_text) || [];

      return {
        dayNumber: nextDay?.day_number,
        title: nextDay?.title,
        references
      };
    } catch (error) {
      console.error('Error getting next reading:', error);
      return null;
    }
  },

  /**
   * Calculate reading streak for user
   */
  async calculateStreak(userReadingPlanId) {
    try {
      const { data, error } = await supabase
        ?.from('user_reading_progress')
        ?.select('completed_at')
        ?.eq('user_reading_plan_id', userReadingPlanId)
        ?.eq('is_completed', true)
        ?.order('completed_at', { ascending: false });

      if (error) throw error;

      if (!data || data?.length === 0) return 0;

      let streak = 0;
      let currentDate = new Date();
      currentDate?.setHours(0, 0, 0, 0);

      for (let i = 0; i < data?.length; i++) {
        const completedDate = new Date(data[i]?.completed_at);
        completedDate?.setHours(0, 0, 0, 0);

        const diffDays = Math.floor((currentDate - completedDate) / (1000 * 60 * 60 * 24));

        if (diffDays === streak) {
          streak++;
        } else if (diffDays > streak) {
          break;
        }
      }

      return streak;
    } catch (error) {
      console.error('Error calculating streak:', error);
      return 0;
    }
  },

  /**
   * Get comprehensive progress statistics for a reading plan
   */
  async getReadingPlanProgressStats(userReadingPlanId) {
    try {
      // Get user reading plan details
      const { data: planData, error: planError } = await supabase
        ?.from('user_reading_plans')
        ?.select(`
          id,
          current_day,
          completed_days,
          start_date,
          reading_plans (
            duration_days
          )
        `)
        ?.eq('id', userReadingPlanId)
        ?.single();

      if (planError) throw planError;

      // Get all progress records
      const { data: progressData, error: progressError } = await supabase
        ?.from('user_reading_progress')
        ?.select('id, is_completed, completed_at')
        ?.eq('user_reading_plan_id', userReadingPlanId);

      if (progressError) throw progressError;

      const totalDays = planData?.reading_plans?.duration_days || 365;
      const currentDay = planData?.current_day || 1;
      const completedDays = planData?.completed_days || 0;

      // Calculate overall completion percentage
      const completionPercentage = (completedDays / totalDays) * 100;

      // Calculate current streak
      let streak = await this.calculateStreak(userReadingPlanId);

      // Calculate days missed
      const startDate = new Date(planData?.start_date);
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      startDate?.setHours(0, 0, 0, 0);
      
      const daysSinceStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1;
      const expectedCompletedDays = Math.min(daysSinceStart, totalDays);
      const missedDays = Math.max(0, expectedCompletedDays - completedDays);

      return {
        currentDay,
        totalDays,
        completedDays,
        completionPercentage: parseFloat(completionPercentage?.toFixed(1)),
        currentStreak: streak,
        missedDays,
        startDate: planData?.start_date,
        daysSinceStart
      };
    } catch (error) {
      console.error('Error getting reading plan progress stats:', error);
      return {
        currentDay: 1,
        totalDays: 365,
        completedDays: 0,
        completionPercentage: 0,
        currentStreak: 0,
        missedDays: 0,
        startDate: new Date()?.toISOString()?.split('T')?.[0],
        daysSinceStart: 1
      };
    }
  },

  /**
   * Enroll user in a reading plan
   */
  async enrollInPlan(userId, planId, config = {}) {
    try {
      const { data, error } = await supabase
        ?.from('user_reading_plans')
        ?.insert({
          user_id: userId,
          reading_plan_id: planId,
          status: 'in_progress',
          current_day: 1,
          completed_days: 0,
          start_date: config?.startDate || new Date()?.toISOString()?.split('T')?.[0],
          preferred_bible_version: config?.bibleVersion?.toLowerCase() || 'kjv',
          email_notifications_enabled: config?.emailNotifications || false,
          catch_up_mode: 'skip',
          started_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();
      
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error enrolling in plan:', error);
      return { error: { message: 'Failed to enroll in reading plan' } };
    }
  },

  /**
   * Get completion statistics for a plan
   */
  async getPlanCompletionStats(planId) {
    try {
      const { data, error } = await supabase
        ?.from('user_reading_plans')
        ?.select('id, status')
        ?.eq('reading_plan_id', planId);

      if (error) throw error;

      const total = data?.length || 0;
      const completed = data?.filter(p => p?.status === 'completed')?.length || 0;

      return {
        totalEnrolled: total,
        totalCompleted: completed,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
      };
    } catch (error) {
      console.error('Error getting completion stats:', error);
      return { totalEnrolled: 0, totalCompleted: 0, completionRate: 0 };
    }
  },

  /**
   * Get Bible verses for a reading plan reference
   * Fetches actual Bible text based on reference details
   */
  async getBibleVersesForReference(reference, bibleVersion = 'kjv') {
    try {
      const { book_name, chapter, verse_start, verse_end } = reference;
      
      if (!book_name || !chapter) {
        return { data: [], error: { message: 'Invalid reference' } };
      }

      // Build query for verses
      let query = supabase
        ?.from('bible_verses')
        ?.select(`
          id,
          verse_number,
          text,
          bible_chapters!inner (
            chapter_number,
            bible_books!inner (
              name,
              abbreviation
            )
          ),
          bible_versions!inner (
            short_name,
            version_name
          )
        `)
        ?.eq('bible_chapters.bible_books.name', book_name)
        ?.eq('bible_chapters.chapter_number', chapter)
        ?.eq('bible_versions.short_name', bibleVersion?.toUpperCase());

      // Add verse range filtering if specified
      if (verse_start && verse_end) {
        query = query?.gte('verse_number', verse_start)?.lte('verse_number', verse_end);
      }
      // If verse_end is blank (null/undefined), get entire chapter regardless of verse_start
      // If no verse_start/verse_end, get entire chapter

      const { data, error } = await query?.order('verse_number', { ascending: true });

      if (error) throw error;

      // Transform data to simpler format
      let verses = data?.map(v => ({
        verse_id: v?.id,
        verse_number: v?.verse_number,
        text: v?.text,
        reference: `${v?.bible_chapters?.bible_books?.name} ${v?.bible_chapters?.chapter_number}:${v?.verse_number}`,
        book_name: v?.bible_chapters?.bible_books?.name,
        chapter: v?.bible_chapters?.chapter_number,
        version: v?.bible_versions?.short_name
      })) || [];

      return { data: verses, error: null };
    } catch (error) {
      console.error('Error fetching Bible verses for reference:', error);
      return { data: [], error: { message: 'Failed to fetch Bible verses' } };
    }
  },

  /**
   * Get all Bible verses for a day's reading
   * Fetches verses for all references in a reading plan day
   */
  async getDayReadingVerses(userReadingPlanId, dayNumber, bibleVersion = 'kjv') {
    try {
      // Step 1: Get the reading_plan_id from the user's enrollment
      const { data: enrollmentData, error: enrollmentError } = await supabase
        ?.from('user_reading_plans')
        ?.select('reading_plan_id')
        ?.eq('id', userReadingPlanId)
        ?.single();

      if (enrollmentError) throw enrollmentError;
      if (!enrollmentData?.reading_plan_id) {
        return { data: null, error: { message: 'Enrollment not found' } };
      }

      // Step 2: Fetch the day record for this plan + day number
      const { data: dayData, error: dayError } = await supabase
        ?.from('reading_plan_days')
        ?.select('id, day_number, title, description')
        ?.eq('reading_plan_id', enrollmentData?.reading_plan_id)
        ?.eq('day_number', dayNumber)
        ?.single();

      if (dayError) throw dayError;
      if (!dayData) {
        return { data: null, error: { message: 'Day not found in plan' } };
      }

      // Step 3: Fetch all references for this day
      const { data: refsData, error: refsError } = await supabase
        ?.from('reading_plan_references')
        ?.select('id, book_name, chapter, verse_start, verse_end, reference_text, display_order')
        ?.eq('reading_plan_day_id', dayData?.id)
        ?.order('display_order');

      if (refsError) throw refsError;

      const references = refsData || [];

      // Step 4: For each reference, fetch the actual Bible verse text
      const referencesWithVerses = await Promise.all(
        references?.map(async (ref) => {
          try {
            const { data: versesData, error: versesError } = await supabase
              ?.rpc('get_chapter_verses', {
                book_name_param: ref?.book_name,
                chapter_num: ref?.chapter,
                version_short_name: bibleVersion?.toUpperCase()
              });

            if (versesError) {
              console.error(`Error fetching verses for ${ref?.reference_text}:`, versesError);
              return { ...ref, verses: [] };
            }

            // Filter to verse range if specified
            // A blank verse_end means the entire chapter is included
            let verses = versesData || [];
            if (ref?.verse_start && ref?.verse_end) {
              // Both start and end specified — filter to exact range
              verses = verses?.filter(v => {
                const vNum = v?.verse_number ?? v?.verse_num;
                return vNum >= ref?.verse_start && vNum <= ref?.verse_end;
              });
            }
            // If verse_end is blank (null/undefined), include the entire chapter (no filtering)

            // Normalize verse shape for ReadingInterface
            const normalizedVerses = verses?.map(v => ({
              verse_id: v?.verse_id ?? v?.id,
              verse_number: v?.verse_number ?? v?.verse_num,
              text: v?.verse_text ?? v?.text,
              reference: v?.reference,
              book_name: v?.book_name ?? ref?.book_name,
              chapter: v?.chapter_number ?? v?.chapter ?? ref?.chapter,
              version: bibleVersion?.toUpperCase()
            }));

            return { ...ref, verses: normalizedVerses };
          } catch (err) {
            console.error(`Error loading verses for ${ref?.reference_text}:`, err);
            return { ...ref, verses: [] };
          }
        })
      );

      return {
        data: {
          title: dayData?.title || `Day ${dayNumber}`,
          description: dayData?.description || null,
          references: referencesWithVerses
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching day reading verses:', error);
      return { data: null, error: { message: error?.message || 'Failed to load reading' } };
    }
  },

  /**
   * Update current day for user's reading plan
   */
  async updateCurrentDay(userReadingPlanId, dayNumber) {
    try {
      const { data, error } = await supabase
        ?.from('user_reading_plans')
        ?.update({
          current_day: dayNumber,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userReadingPlanId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating current day:', error);
      return { error: { message: 'Failed to update current day' } };
    }
  },

  /**
   * Get progress for a specific day (which references are completed)
   */
  async getDayProgress(userReadingPlanId, dayNumber) {
    try {
      // Get the reading_plan_id from enrollment
      const { data: enrollmentData, error: enrollmentError } = await supabase
        ?.from('user_reading_plans')
        ?.select('reading_plan_id')
        ?.eq('id', userReadingPlanId)
        ?.single();

      if (enrollmentError) throw enrollmentError;

      // Get the day record
      const { data: dayData, error: dayError } = await supabase
        ?.from('reading_plan_days')
        ?.select('id')
        ?.eq('reading_plan_id', enrollmentData?.reading_plan_id)
        ?.eq('day_number', dayNumber)
        ?.single();

      if (dayError || !dayData) return { completed: [], total: 0, percentage: 0 };

      // Get all reference IDs for this day
      const { data: refsData, error: refsError } = await supabase
        ?.from('reading_plan_references')
        ?.select('id')
        ?.eq('reading_plan_day_id', dayData?.id);

      if (refsError) throw refsError;

      const referenceIds = refsData?.map(r => r?.id) || [];
      if (referenceIds?.length === 0) return { completed: [], total: 0, percentage: 0 };

      // Get completed references
      const { data: progressData, error: progressError } = await supabase
        ?.from('user_reading_progress')
        ?.select('reading_plan_reference_id')
        ?.eq('user_reading_plan_id', userReadingPlanId)
        ?.in('reading_plan_reference_id', referenceIds)
        ?.eq('is_completed', true);

      if (progressError) throw progressError;

      const completedIds = progressData?.map(p => p?.reading_plan_reference_id) || [];

      return {
        completed: completedIds,
        total: referenceIds?.length,
        percentage: referenceIds?.length > 0 ? (completedIds?.length / referenceIds?.length) * 100 : 0
      };
    } catch (error) {
      console.error('Error getting day progress:', error);
      return { completed: [], total: 0, percentage: 0 };
    }
  },

  /**
   * Mark a reading plan reference as complete
   */
  async markReferenceComplete(userReadingPlanId, referenceId) {
    try {
      const { data, error } = await supabase
        ?.from('user_reading_progress')
        ?.upsert({
          user_reading_plan_id: userReadingPlanId,
          reading_plan_reference_id: referenceId,
          is_completed: true,
          completed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }, {
          onConflict: 'user_reading_plan_id,reading_plan_reference_id'
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error marking reference complete:', error);
      return { data: null, error: { message: error?.message || 'Failed to mark complete' } };
    }
  },

  /**
   * Check if a day is completed
   */
  async isDayCompleted(userReadingPlanId, dayNumber) {
    try {
      const progress = await this.getDayProgress(userReadingPlanId, dayNumber);
      return progress?.total > 0 && progress?.completed?.length === progress?.total;
    } catch (error) {
      console.error('Error checking day completion:', error);
      return false;
    }
  }
};

// =============================================================================
// USER PROFILE SERVICES
// =============================================================================

// User Profile Management
export const userProfileService = {
  /**
   * Get complete user profile with all settings
   */
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      // Handle case where profile doesn't exist yet (PGRST116 error)
      if (error) {
        if (error?.code === 'PGRST116') {
          // Profile doesn't exist yet, return null
          return null;
        }
        throw error;
      }

      // Transform database snake_case to camelCase for React
      return {
        id: data?.id,
        username: data?.username || '',
        firstName: data?.first_name || '',
        lastName: data?.last_name || '',
        email: data?.email || '',
        fullName: data?.full_name || '',
        reflectionVisibility: data?.reflection_visibility || 'private',
        notificationPreferences: data?.notification_preferences || {
          dailyReminders: true,
          studyUpdates: false,
          communityActivity: true
        },
        reminderSchedule: data?.reminder_schedule || '08:00',
        memorization: {
          phase1Days: data?.phase_1_days || 30,
          dailyReviewDays: data?.daily_review_days || 60,
          weeklyReviewWeeks: data?.weekly_review_weeks || 6,
          monthlyReviewMonths: data?.monthly_review_months || 3,
          addToMemorizationIntervalDays: data?.verse_addition_interval_days || 7
        },
        avatar: {
          config: data?.avatar_config || {},
          seed: data?.avatar_seed || '',
          style: data?.avatar_style || 'open-peeps',
          svg: data?.avatar_svg || '',
          svgUrl: data?.avatar_svg_url || '',
          storagePath: data?.avatar_storage_path || ''
        },
        stats: {
          versesMemorized: data?.total_verses_memorized || 0,
          studyStreak: data?.streak_count || 0
        },
        languagePreference: data?.language_preference || 'en',
        timezone: data?.timezone || 'America/New_York',
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  /**
   * Update user profile settings
   */
  async updateUserProfile(userId, updates) {
    try {
      // First, get the current profile to compare values
      const { data: currentProfile } = await supabase?.from('user_profiles')?.select('username')?.eq('id', userId)?.single();

      // Transform camelCase to snake_case for database
      const dbUpdates = {};

      // Normalize username values (treat empty string and null as equivalent)
      const normalizeUsername = (val) => val || null;
      const currentUsername = normalizeUsername(currentProfile?.username);
      const newUsername = normalizeUsername(updates?.username);

      // Only include username if it has actually changed (prevents duplicate key error)
      if (updates?.username !== undefined && newUsername !== currentUsername) {
        dbUpdates.username = updates?.username;
      }
      if (updates?.firstName !== undefined) dbUpdates.first_name = updates?.firstName;
      if (updates?.lastName !== undefined) dbUpdates.last_name = updates?.lastName;
      if (updates?.email !== undefined) dbUpdates.email = updates?.email;
      if (updates?.fullName !== undefined) dbUpdates.full_name = updates?.fullName;
      if (updates?.reflectionVisibility !== undefined) dbUpdates.reflection_visibility = updates?.reflectionVisibility;
      if (updates?.notificationPreferences !== undefined) dbUpdates.notification_preferences = updates?.notificationPreferences;
      if (updates?.reminderSchedule !== undefined) dbUpdates.reminder_schedule = updates?.reminderSchedule;
      if (updates?.timezone !== undefined) dbUpdates.timezone = updates?.timezone;

      // Handle language preference
      if (updates?.languagePreference !== undefined) dbUpdates.language_preference = updates?.languagePreference;

      // Handle memorization settings
      if (updates?.memorization) {
        if (updates?.memorization?.phase1Days !== undefined) dbUpdates.phase_1_days = updates?.memorization?.phase1Days;
        if (updates?.memorization?.dailyReviewDays !== undefined) dbUpdates.daily_review_days = updates?.memorization?.dailyReviewDays;
        if (updates?.memorization?.weeklyReviewWeeks !== undefined) dbUpdates.weekly_review_weeks = updates?.memorization?.weeklyReviewWeeks;
        if (updates?.memorization?.monthlyReviewMonths !== undefined) dbUpdates.monthly_review_months = updates?.memorization?.monthlyReviewMonths;
        if (updates?.memorization?.addToMemorizationIntervalDays !== undefined) dbUpdates.verse_addition_interval_days = updates?.memorization?.addToMemorizationIntervalDays;
      }

      // Handle avatar updates
      if (updates?.avatar) {
        if (updates?.avatar?.config !== undefined) dbUpdates.avatar_config = updates?.avatar?.config;
        if (updates?.avatar?.seed !== undefined) dbUpdates.avatar_seed = updates?.avatar?.seed;
        if (updates?.avatar?.style !== undefined) dbUpdates.avatar_style = updates?.avatar?.style;
        if (updates?.avatar?.svg !== undefined) dbUpdates.avatar_svg = updates?.avatar?.svg;
        if (updates?.avatar?.svgUrl !== undefined) dbUpdates.avatar_svg_url = updates?.avatar?.svgUrl;
        if (updates?.avatar?.storagePath !== undefined) dbUpdates.avatar_storage_path = updates?.avatar?.storagePath;
      }

      // Handle chunking settings
      if (updates?.chunkingSettings !== undefined) {
        dbUpdates.chunking_settings = updates?.chunkingSettings;
      }

      const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', userId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  /**
   * Get user chunking settings
   */
  async getChunkingSettings(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('chunking_settings')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;

      // Return settings with defaults if not set
      return data?.chunking_settings || {
        chunkSize: 3,
        repetitions: 5,
        autoplay: false,
        voiceSpeed: 'slow',
        voiceGender: 'female',
        volume: 1
      };
    } catch (error) {
      console.error('Error fetching chunking settings:', error);
      // Return defaults on error
      return {
        chunkSize: 3,
        repetitions: 5,
        autoplay: false,
        voiceSpeed: 'slow',
        voiceGender: 'female',
        volume: 1
      };
    }
  },

  /**
   * Update user chunking settings
   */
  async updateChunkingSettings(userId, settings) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ chunking_settings: settings })
        ?.eq('id', userId)
        ?.select('chunking_settings')
        ?.single();

      if (error) throw error;

      return data?.chunking_settings;
    } catch (error) {
      console.error('Error updating chunking settings:', error);
      throw error;
    }
  }
};

// =============================================================================
// REAL-TIME SUBSCRIPTIONS
// =============================================================================

export const subscriptionService = {
  subscribeToUserVerses(userId, callback) {
    const subscription = supabase
      ?.channel('user_verses')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'verses',
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      ?.subscribe();

    return subscription;
  },

  subscribeToUserProgress(userId, callback) {
    const subscription = supabase
      ?.channel('user_progress')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_progress',
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      ?.subscribe();

    return subscription;
  },

  unsubscribe(subscription) {
    if (subscription) {
      return supabase?.removeChannel(subscription);
    }
  }
};